import { useEffect, useState } from "react";
import { Card, Col, Form, ProgressBar, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import { Helmet } from "react-helmet";

import type { ServiceBusStatusDto } from "Src/api/Dto";

import { getServiceBusStatus, startServiceBus, stopServiceBus } from "Src/api/Admin";

import { useError } from "Src/components/Common/ErrorBoundary";

interface State {
  loading: boolean;
  status: ServiceBusStatusDto | null;
}

type MessageDirection = "Inkommande" | "Utgående";

const messageLimitOptions = [10, 25, 50, 100];

const knownOutgoingMessageTypes = [
  "BookCreated",
  "BookUpdated",
  "BookDeleted",
  "MetadataUpdated",
  "ContentUploaded",
  "DeleteContent",
  "AssignmentCreated",
  "AssignmentUpdated",
  "AssignmentActivated",
  "AssignmentEnded",
  "SignedAgreementCreated",
  "SignedAgreementUpdated",
  "SignedAgreementDeleted"
];

export function Index() {
  const [{ loading, status }, setState] = useState<State>({
    loading: true,
    status: null
  });
  const [messageDirection, setMessageDirection] = useState<MessageDirection>("Inkommande");
  const [messageLimit, setMessageLimit] = useState(messageLimitOptions[0]);

  const dispatchError = useError();
  const directionStatus = messageDirection === "Inkommande" ? status?.incoming : status?.outgoing;
  const messageTypeStats = knownOutgoingMessageTypes.map((messageType) => ({
    messageType,
    count: directionStatus?.processedByMessageType.find((item) => item.messageType === messageType)?.count ?? 0
  }));
  const maxMessageTypeCount = Math.max(...messageTypeStats.map((messageType) => messageType.count), 1);
  const incomingSummaryStats = [
    { label: "Meddelanden i kö", count: status?.incoming.messageCount ?? 0, variant: "primary" },
    { label: "Behandlade händelser", count: status?.incoming.processedCount ?? 0, variant: "success" },
    { label: "Dead-letter", count: status?.incoming.dlqCount ?? 0, variant: "danger" }
  ];
  const maxIncomingSummaryCount = Math.max(...incomingSummaryStats.map((stat) => stat.count), 1);
  const messageDirectionLabel = messageDirection === "Inkommande" ? "behandlade" : "publicerade";
  const messagesHeading = `Senast ${messageLimit} ${messageDirectionLabel} händelser`;
  const messagesHelpText =
    messageDirection === "Inkommande"
      ? "De senaste 100 behandlade händelserna lagras i minnet och rensas vid omstart."
      : "De senaste 100 publicerade händelserna lagras i minnet och rensas vid omstart.";

  const loadStatus = async () => {
    const status = await getServiceBusStatus();
    setState({ loading: false, status });
  };

  useEffect(() => {
    let active = true;
    let fetching = false;

    const load = async () => {
      if (fetching) {
        return;
      }
      fetching = true;
      try {
        const status = await getServiceBusStatus();
        if (active) {
          setState({ loading: false, status });
        }
      } catch (error) {
        if (active) {
          dispatchError(error);
        }
      } finally {
        fetching = false;
      }
    };

    load();
    const interval = window.setInterval(load, 5000);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [dispatchError]);

  const toggleProcessing = async () => {
    try {
      if (status?.incoming.isProcessing) {
        await stopServiceBus();
      } else {
        await startServiceBus();
      }
      await loadStatus();
    } catch (error) {
      dispatchError(error);
    }
  };

  return (
    <>
      <Helmet title="Händelser" />
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-end mb-4">
          <h2 className="mb-0">Händelser</h2>
          <Tabs
            activeKey={messageDirection}
            className="ms-auto"
            onSelect={(key) => {
              if (key === "Inkommande" || key === "Utgående") {
                setMessageDirection(key);
              }
            }}
          >
            <Tab eventKey="Inkommande" title="Behandlade händelser" />
            <Tab eventKey="Utgående" title="Publicerade händelser" />
          </Tabs>
        </div>
        {loading || !status ? (
          <Row className="justify-content-center">
            <Spinner animation="border" />
          </Row>
        ) : (
          <>
            {messageDirection === "Inkommande" && (
              <div className="d-flex justify-content-start align-items-center gap-2 mb-3">
                <span className="text-muted">Bearbeta inkommande händelser</span>
                <Form.Check
                  checked={directionStatus?.isProcessing ?? false}
                  className="service-bus-switch"
                  id="service-bus-processing-switch"
                  type="switch"
                  onChange={toggleProcessing}
                />
              </div>
            )}
            <h3 className="mb-3">Översikt</h3>
            <Row className="g-4 mb-4">
              {messageDirection === "Inkommande" ? (
                <Col xs={12}>
                  <Row className="g-3">
                    {incomingSummaryStats.map((stat) => (
                      <Col key={stat.label} md={4}>
                        <div>
                          <div className="d-flex justify-content-between mb-1">
                            <span>{stat.label}</span>
                            <strong>{stat.count}</strong>
                          </div>
                          <ProgressBar
                            className="opacity-75"
                            max={maxIncomingSummaryCount}
                            now={stat.count}
                            style={{ height: "0.5rem" }}
                            variant={stat.variant}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
              ) : (
                <Col xs={12}>
                  <Row className="g-3">
                    {messageTypeStats.map((messageType, index) => (
                      <Col key={messageType.messageType} md={4}>
                        <div>
                          <div className="d-flex justify-content-between mb-1">
                            <span>{messageType.messageType}</span>
                            <strong>{messageType.count}</strong>
                          </div>
                          <ProgressBar
                            className="opacity-75"
                            max={maxMessageTypeCount}
                            now={messageType.count}
                            style={{ height: "0.5rem" }}
                            variant={["primary", "success", "info", "warning", "danger"][index % 5]}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
              )}
            </Row>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h3 className="mb-0">{messagesHeading}</h3>
                <small className="text-muted">{messagesHelpText}</small>
              </div>
              <Form.Select
                aria-label="Antal händelser att visa"
                className="w-auto"
                value={messageLimit}
                onChange={(event) => setMessageLimit(Number(event.currentTarget.value))}
              >
                {messageLimitOptions.map((limit) => (
                  <option key={limit} value={limit}>
                    Visa {limit}
                  </option>
                ))}
              </Form.Select>
            </div>
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>ISBN</th>
                  <th>Bokinfo ID</th>
                  {messageDirection === "Utgående" && <th>Återförsäljare ID</th>}
                  <th>Avsändare</th>
                  <th>Status</th>
                  <th>Behandlad</th>
                  <th>Bearbetningstid</th>
                </tr>
              </thead>
              <tbody>
                {directionStatus?.recentlyProcessed.slice(0, messageLimit).map((message) => (
                  <tr key={`${message.direction}-${message.messageId}-${message.publisherBrId}-${message.vendorBrId}`}>
                    <td>{message.messageType}</td>
                    <td>{message.isbn}</td>
                    <td>{message.publisherBrId}</td>
                    {messageDirection === "Utgående" && <td>{message.vendorBrId}</td>}
                    <td>{message.producer}</td>
                    <td>{message.status}</td>
                    <td>{new Date(message.processedAt).toLocaleString("sv-SE")}</td>
                    <td>{message.processingTimeMs} ms</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card>
    </>
  );
}
